import { useState, useCallback, useRef } from "react";
import apiV2 from "api-v2";
import { CancelFileUploadRoute } from "helpers/api/apiRoutes";
import { safeRandomUUID } from "newui/utils/general";

const useChunkFileUpload = ({
	url = "/file-upload/store",
	chunkSize = 1024 * 1024,
	maxRetries = 3,
	// The hook prioritizes this callback if provided
	onFileUploadComplete,
	// Default call back: fallback if onFileUploadComplete is not provided
	handleFileUploadComplete,
}) => {
	const [progress, setProgress] = useState(0);
	const [isUploadCompleted, setIsUploadCompleted] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState(null);
	const controllersRef = useRef(new Map());

	const uploadChunk = async (
		formData,
		chunkIndex,
		totalChunks,
		retries = 0,
		uploadId
	) => {
		const signal = controllersRef.current.get(uploadId)?.signal;
		try {
			const res = await apiV2.post(url, formData, {
				headers: { "Content-Type": "multipart/form-data" },
				signal: signal,
			});
			setProgress(() =>
				parseFloat((((chunkIndex + 1) / totalChunks) * 100).toFixed(1))
			);
			return res;
		} catch (err) {
			if (err.code === "ERR_CANCELED") {
				controllersRef.current.delete(uploadId);
				uploadCancelled(uploadId);
				throw err;
			}

			if (retries < maxRetries) {
				return uploadChunk(
					formData,
					chunkIndex,
					totalChunks,
					retries + 1,
					uploadId
				);
			} else {
				controllersRef.current.delete(uploadId);
				throw err;
			}
		}
	};

	const uploadFile = useCallback(
		async (file, extraData) => {
			if (file && file instanceof File) {
				const totalChunks = Math.ceil(file.size / chunkSize);
				setProgress(0);
				setError(null);
				setIsUploading(true);

				try {
					let uploadId = safeRandomUUID();
					file.uploadId = uploadId;
					controllersRef.current.set(uploadId, new AbortController());
					for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
						const start = chunkIndex * chunkSize;
						const end = Math.min(start + chunkSize, file.size);
						const chunk = file.slice(start, end);

						const formData = new FormData();
						formData.append("file", chunk);
						formData.append("resumableChunkNumber", chunkIndex);
						formData.append("resumableTotalChunks", totalChunks);
						formData.append("originalName", file.name);
						formData.append("uploadId", uploadId);
						for (let key in extraData) {
							formData.append(key, extraData[key]);
						}
						const res = await uploadChunk(
							formData,
							chunkIndex,
							totalChunks,
							0,
							uploadId
						);

						if (chunkIndex + 1 === totalChunks) {
							const responseData = res?.data?.data ?? res?.data;

							// first try onFileUploadComplete if provided
							if (typeof onFileUploadComplete === "function") {
								onFileUploadComplete(responseData);
							}
							// then try handleFileUploadComplete if provided
							else if (typeof handleFileUploadComplete === "function") {
								handleFileUploadComplete(responseData);
							} else {
								console.warn(
									"useChunkFileUpload: No callback function provided"
								);
							}
						}
					}
					setIsUploadCompleted(true);
					setIsUploading(false);
				} catch (err) {
					setError(err);
				}
			}
		},
		
		[
			url,
			chunkSize,
			isUploadCompleted,
			onFileUploadComplete,
			handleFileUploadComplete,
		]
	);

	const uploadCancelled = async (uploadId) => {
		apiV2.post(CancelFileUploadRoute, { uploadId: uploadId });
	};

	const cancelUpload = useCallback((uploadId) => {
		const controller = controllersRef.current.get(uploadId);
		if (controller) {
			controller.abort();
			setIsUploading(false);
			setError(new Error("Upload cancelled by user"));
		}
	}, []);

	return {
		uploadFile,
		progress,
		error,
		setError,
		isUploading,
		isUploadCompleted,
		cancelUpload,
	};
};

export default useChunkFileUpload;
